// The admitted presentation tokens: the only colours and spacings a direct edit
// (ARCH-C2-PRESENTATION) may set on an Artifact or app slot. DES1 owns this table.
//
// ART, INS, the viewers and the export writers bind to it by POLICY.id and
// POLICY.digest, never by copying values. Writers can't read CSS variables, so
// each colour carries its resolved light and dark sRGB value; the interface keeps
// using var(--pc-<cssVar>). test/design-presentation-tokens-unit.mjs checks
// that every cssVar exists in the token sheets, that the digest matches the
// table, and that text colours clear 4.5:1 on paper and surface in both themes.
//
// IMMUTABLE BY VERSION. Changing a value, adding a key or changing a channel
// list means a new POLICY.id (…/2) and a new digest; existing stored overrides
// keep naming the policy they were made under.

export const POLICY = Object.freeze({
  id: 'poneva.presentation-tokens/2',
  digest: 'sha256:c69d1f08bb292e95b1d3023b3d427e5db64d2abd561da17904e06f4757c402ba',
});

// Channels a colour key may be used for. A slot's manifest narrows this further.
const TEXT = 'text';
const BACKGROUND = 'background';
const BORDER = 'border';
const FILL = 'fill';
const STROKE = 'stroke';

const colour = (cssVar, light, dark, channels) =>
  Object.freeze({ cssVar, light, dark, channels: Object.freeze(channels) });

export const COLOURS = Object.freeze({
  ink: colour('ink', '#292723', '#eee9e0', [TEXT, BORDER, FILL, STROKE]),
  muted: colour('muted', '#70665b', '#b6aa9a', [TEXT, STROKE]),
  paper: colour('paper', '#faf9f7', '#211f1c', [BACKGROUND, FILL]),
  surface: colour('surface', '#fffefd', '#292621', [BACKGROUND, FILL]),
  soft: colour('soft', '#eeeae3', '#37322b', [BACKGROUND, FILL]),
  line: colour('line', '#e8e5df', '#403b34', [BORDER, STROKE]),
  'line-strong': colour('line-strong', '#938e89', '#787470', [BORDER, STROKE]),
  success: colour('success', '#3f6b4b', '#9cc3a5', [TEXT, BORDER, FILL, STROKE]),
  warning: colour('warning', '#8c5d00', '#e2a73b', [TEXT, BORDER, FILL, STROKE]),
  danger: colour('danger', '#a54538', '#f1a99e', [TEXT, BORDER, FILL, STROKE]),
  'success-bg': colour('success-bg', '#e7ebe6', '#36362f', [BACKGROUND, FILL]),
  'warning-bg': colour('warning-bg', '#f1eae1', '#3c3426', [BACKGROUND, FILL]),
  'danger-bg': colour('danger-bg', '#f6e7e4', '#3e342e', [BACKGROUND, FILL]),
  // Categorical series: chart identity, assigned in order and never cycled.
  'series-1': colour('series-1', '#3a6cc0', '#5f8bd6', [FILL, STROKE]),
  'series-2': colour('series-2', '#c9622f', '#d27242', [FILL, STROKE]),
  'series-3': colour('series-3', '#10937a', '#239d84', [FILL, STROKE]),
  'series-4': colour('series-4', '#b98a10', '#b58a1f', [FILL, STROKE]),
  'series-5': colour('series-5', '#a3569b', '#b56dad', [FILL, STROKE]),
  'series-6': colour('series-6', '#5f8f2a', '#72a040', [FILL, STROKE]),
  'series-7': colour('series-7', '#5b4fb3', '#8174d6', [FILL, STROKE]),
  'series-8': colour('series-8', '#c0443a', '#d15c52', [FILL, STROKE]),
});

// Spacing tokens, in px. A slot may also take a literal 0–256 px; these are the
// named steps the property sheet offers first. /2 drops the 'none' step: INS
// reserves that spelling for absence, so 0 px is set as the literal 0.
export const SPACING = Object.freeze({
  xs: Object.freeze({ cssVar: '1', px: 4 }),
  s: Object.freeze({ cssVar: '2', px: 8 }),
  m: Object.freeze({ cssVar: '4', px: 16 }),
  l: Object.freeze({ cssVar: '6', px: 24 }),
  xl: Object.freeze({ cssVar: '8', px: 32 }),
  '2xl': Object.freeze({ cssVar: '10', px: 40 }),
});

export const SPACING_LITERAL = Object.freeze({ min: 0, max: 256, unit: 'px' });

// The canonical form a digest is computed over: keys sorted, no whitespace.
function canonicalOf(table) {
  const sort = (value) =>
    Array.isArray(value)
      ? value.map(sort)
      : value && typeof value === 'object'
        ? Object.fromEntries(
            Object.keys(value)
              .sort()
              .map((key) => [key, sort(value[key])]),
          )
        : value;
  return JSON.stringify(
    sort({
      id: table.id,
      colours: table.colours,
      spacing: table.spacing,
      literal: table.literal,
    }),
  );
}

export function canonicalTable() {
  return canonicalOf({
    id: POLICY.id,
    colours: COLOURS,
    spacing: SPACING,
    literal: SPACING_LITERAL,
  });
}

// Every policy ever issued, by id. Saved builds, manifests and exports resolve the
// policy they name here and are never silently rebound to the current one.
// Entries are frozen once published; a new policy adds an entry.
const V1_SPACING = Object.freeze({
  none: Object.freeze({ cssVar: null, px: 0 }),
  ...SPACING,
});

export const POLICIES = Object.freeze({
  'poneva.presentation-tokens/1': Object.freeze({
    id: 'poneva.presentation-tokens/1',
    digest: 'sha256:5f92858504ea58d5f9999b975032506283d67a60590c0563e6e991500315790d',
    colours: COLOURS,
    spacing: V1_SPACING,
    literal: SPACING_LITERAL,
  }),
  [POLICY.id]: Object.freeze({
    ...POLICY,
    colours: COLOURS,
    spacing: SPACING,
    literal: SPACING_LITERAL,
  }),
});

export function resolvePolicy(id) {
  const policy = Object.hasOwn(POLICIES, id) ? POLICIES[id] : null;
  if (!policy) throw Error('unknown_presentation_policy');
  return policy;
}

export function policyCanonical(id) {
  return canonicalOf(resolvePolicy(id));
}

export function colourKeysFor(channel) {
  return Object.keys(COLOURS).filter((key) => COLOURS[key].channels.includes(channel));
}
